/**
 * @Author: zachalam
 * @Date:   2017-01-23T16:03:15-08:00
 * @Last modified by:   zachalam
 * @Last modified time: 2017-01-24T11:47:06-08:00
 */

import React from 'react';
import axios, { AxiosPromise } from 'axios';
import qs from 'qs';
import store from './store';
import {
  searchMicroserviceKey,
  searchMicroserviceUrl,
  api,
  per_page as perPage,
  searchMicroserviceEnabled,
} from './config/Master';
import { cleanObject } from './common';
import { logoutUserAction, refreshUserToken } from './actions/authActions';
import { displayFailMessage } from './actions/notificationActions';
import { storeSpecificErrors, killLoading } from './actions/commonActions';
import { getCookie } from './utils/helpers';
import SearchConfig from './config/Search';
import { WEB_TIMESTAMP, IS_REFRESH_TOKEN_COOKIE } from './config/constants';
import TextLink from './components/common/TextLinkV2';
import * as auth from './sdk/AuthSDK';
import * as billing from './sdk/BillingSDK';
import * as favourites from './sdk/FavSDK';
import * as offer from './sdk/OfferSDK';
import * as profile from './sdk/ProfileSDK';
import * as webAuthn from './sdk/WebAuthnSDK';
import * as mail from './sdk/MailSDK';
import * as gallery from './sdk/GallerySDK';
import * as mixpanel from './sdk/MixpanelSDK';
import * as common from './sdk/CommonSDK';
import * as settings from './sdk/SettingsSDK';
import { getUniqueDevice } from './utils/ipQualityScore';
import { setMailError } from './actions/mailActions';
import { setShowSelfieVerificationWall } from './actions/profileActions';
import { getAccessToken } from './utils/tokenUtils';

// check for PRODUCTION defined api, if so use that instead.

const handleErrorCode = (code: any, err: any): any => {
  switch (code) {
    case 42204001:
      store.dispatch(
        displayFailMessage({
          info: (
            <span>
              Too many open offers or requests, please{' '}
              <TextLink href="/billing">
                <span>purchase credits</span>
              </TextLink>{' '}
              to continue.
            </span>
          ),
        })
      );
      break;
    case 40419001: {
      let theError;

      try {
        const displayError = err.description;

        if (displayError) theError = displayError;
        else {
          theError = 'Invalid email/password combination.';
        }
      } catch (e) {
        console.warn(e);
        theError = 'Please enter an email and password.';
      }

      store.dispatch(storeSpecificErrors('social', theError));
      // hide loading
      store.dispatch(killLoading());
      break;
    }
    // Don't show notification banner
    case 40101001:
    case 40101006:
    case 40101007:
    case 40101010:
    case 40422001:
    case 40422002:
    case 40101011:
    case 40101015:
    case 40101014:
    case 40125004:
    case 40125005:
    case 40125006:
      break;

    default:
      store.dispatch(
        displayFailMessage({
          info: err.description,
        })
      );
      break;
  }
};

const interceptError = (error: any): boolean | null => {
  const { response } = error;
  const { data, status } = response;

  const noErrorData =
    typeof data.errors === 'undefined' ||
    typeof data.errors[0] === 'undefined' ||
    typeof data.errors[0].code === 'undefined';

  if (typeof data.error !== 'undefined' && noErrorData) {
    if (data.error === 'Too Many Requests') {
      store.dispatch(storeSpecificErrors('form', { errorCode: 42920003 }));
    } else if (data.error === 'Sorry, something went wrong.' && status != 401) {
      store.dispatch(storeSpecificErrors('form', { errorCode: 40120001 }));
    }
  }

  if (typeof data.email !== 'undefined' && noErrorData) {
    const errorText = data.email[0];
    const regex = RegExp(/Too many login attempts/i);

    if (regex.test(errorText)) {
      store.dispatch(storeSpecificErrors('form', { errorCode: 42901001 }));
    }
  }

  if (typeof status !== 'undefined' && status === 429) {
    store.dispatch(
      displayFailMessage({
        info: 'Woah! Something went wrong. Please wait a moment and try again.',
      })
    );
  }

  if (noErrorData) {
    return null;
  }

  const err = data.errors[0];

  if ([40101016, 42214001].includes(err.code)) {
    store.dispatch(setShowSelfieVerificationWall(true));
    return true;
  }

  if ([40022005, 40024001].includes(err.code)) {
    return true;
  } else {
    handleErrorCode(err.code, err);
  }

  return true;
};

// http://jira.infostreamgroup.com/browse/WYP-6441
axios.defaults.withCredentials = true;

axios.interceptors.request.use(
  config => {
    if (!config.url) {
      return config;
    }

    // attach auth headers only on our api only
    if (config.url.indexOf(api) > -1) {
      const { virgil, jwToken } = store.getState().auth;

      config.headers.Accept = 'application/json';

      if (jwToken) {
        config.headers.Authorization = 'Bearer ' + jwToken;
      }

      if (WEB_TIMESTAMP) {
        config.headers['web-timestamp'] = WEB_TIMESTAMP;
      }

      if (virgil) {
        config.headers['X-Auth-Type'] = 'virgil';
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Token refresh promise cache to prevent duplicate refresh calls
let tokenRefreshPromise: Promise<any> | null = null;

const refreshAuthToken = async (error: any): Promise<any> => {
  const originalRequest = error.config;

  if (!store.getState().auth.isAuthenticated) {
    return Promise.reject(error);
  }

  // If token refresh is already in progress, wait for it to complete
  if (tokenRefreshPromise) {
    try {
      const token = await tokenRefreshPromise;

      // Retry the original request with the new token
      return axios({
        ...originalRequest,
        headers: {
          ...originalRequest.headers,
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      return Promise.reject(error);
    }
  }

  // Start a new token refresh
  let tokenPath = 'refresh';

  // Check if the new is_refresh_token exist in the Cookie
  if (getCookie(IS_REFRESH_TOKEN_COOKIE)) {
    tokenPath = 'refresh-token';
  }

  // Create the refresh promise and cache it
  tokenRefreshPromise = refreshUserToken(tokenPath)
    .then(token => {
      tokenRefreshPromise = null;
      return token;
    })
    .catch(err => {
      tokenRefreshPromise = null;
      throw err;
    });

  try {
    const token = await tokenRefreshPromise;

    // Retry the original request with the new token
    return axios({
      ...originalRequest,
      headers: {
        ...originalRequest.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    return Promise.reject(error);
  }
};

const checkUserAuth = (error: any): any => {
  if (error.response.data.message !== undefined && error.response.data.message === 'Token has expired') {
    // attempt to get a new token if the user is authenticated...
    // the API said the user was authenticated, if they were previously
    // authenticated attempt to refresh their token.
    if (store.getState().auth.isAuthenticated) {
      // The auth token has expired. Refresh token and resend request
      return refreshAuthToken(error);
    }
  }
};

const needTokenRefresh = (error: any): any => {
  const typing = error.response.request.responseURL.indexOf('typing-indicator') > -1;

  if (typing && error.response.data && error.response.data.error && error.response.data.error.code === 40301066) {
    if (store.getState().auth.isAuthenticated) {
      // The auth token has expired. Refresh token and resend request
      return refreshAuthToken(error);
    }
  }
};

const reloadAndFetchUpdatedResource = (error: any): any => {
  // refresh the whole page to get the new bundle
  if (typeof error.response.headers['web-timestamp'] !== 'undefined') {
    setTimeout(() => {
      window.location.reload();
    }, 2000); // give 2000 to sync the api version to reducer
  }
};

const handleFormError = (error: any): any => {
  const paymentVisit = error.response.request.responseURL.indexOf('payment/visited') > -1;
  const verifyPhonePassword = error.response.request.responseURL.indexOf('account/verify-password') > -1;
  const requestOtp = error.response.request.responseURL.indexOf('otp') > -1;
  const requestChangeEmailPassAuth =
    error.response.request.responseURL.indexOf('settings/verify-password') > -1 ||
    error.response.request.responseURL.indexOf('settings/validate-email') > -1;
  const errorData = error.response.data;

  if (
    errorData === undefined ||
    errorData.data === undefined ||
    errorData.data.source === undefined ||
    !(errorData.data.source === 'gateway' || errorData.data.source[0] === 'offer')
  ) {
    if (!paymentVisit && !verifyPhonePassword && !requestOtp && !requestChangeEmailPassAuth) {
      const isU2pCountryError =
        !!errorData.use2pay_billing_country &&
        errorData.use2pay_billing_country[0] === 'Use2pay billing country is not a valid option.';

      store.dispatch(
        displayFailMessage({
          info: isU2pCountryError
            ? 'Billing country is not valid. Please try again.'
            : 'There was an error submitting the form. Please try again.',
        })
      );
    }
  }
};

const handleBadRequest = (error: any, login: any, logout: any) => {
  const errorMessage = error.response.data.error;
  const { data } = error.response;

  if (
    typeof data.message !== 'undefined' &&
    data.message === 'Your ID verification status is not allowed to access this resource.'
  ) {
    return;
  }

  if (
    (error.response.request.responseURL.indexOf('account/deactivate') > -1 ||
      error.response.request.responseURL.indexOf('account/gdpr_delete') > -1) &&
    error.response.data.message !== undefined &&
    error.response.data.message === 'Invalid captcha.'
  ) {
    store.dispatch(
      displayFailMessage({
        info: 'Invalid captcha.',
      })
    );

    return;
  }

  if (
    error.response.data.message !== undefined &&
    (error.response.data.message === 'The token could not be parsed from the request' ||
      error.response.data.message === 'The token has been blacklisted')
  ) {
    if (!login && !logout) {
      store.dispatch(logoutUserAction(null, false));
    }
  } else if (errorMessage === 'Unauthorized') {
    store.dispatch(logoutUserAction(null, false));
  } else {
    if (
      error?.response?.data?.errors &&
      error?.response?.data?.errors.length > 0 &&
      [40022005].includes(error.response.data.errors[0].code)
    ) {
      return;
    } else {
      const ignoreMessages = ['OTP_REQUEST_MAX_ATTEMPT', 'OTP_RETRY_MAX_ATTEMPT'];

      if (!ignoreMessages.includes(error.response.data.message)) {
        store.dispatch(
          displayFailMessage({
            info: 'Uh oh, something went wrong. Please refresh the page',
          })
        );
      }
    }
  }
};

const handleTooManyRequest = (error: any): any => {
  if (
    error.response.data &&
    error.response.data.error &&
    error.response.data.error.description &&
    [42920004].indexOf(error.response.data.error.code) > -1
  ) {
    store.dispatch(
      displayFailMessage({
        info: error.response.data.error.description,
      })
    );
  }
};

const handleErrorStatus = (error: any): any => {
  const status = error.response && error.response.status;
  const errorMessage = error.response.data.error;
  const login =
    error.response.request.responseURL.indexOf('auth/login') > -1 ||
    error.response.request.responseURL.indexOf('auth/email/check') > -1;
  const logout = error.response.request.responseURL.indexOf('auth/logout') > -1;
  const otpVerify =
    error.response.request.responseURL.indexOf('otp/verify') > -1 ||
    error.response.request.responseURL.indexOf('otp/auth/verify') > -1;

  switch (status) {
    case 401: {
      const tokenPresent = checkUserAuth(error);

      if (tokenPresent) return tokenPresent;

      if (errorMessage === 'Unauthorized') {
        store.dispatch(logoutUserAction(null, false));
      }

      if (!login && !logout && !otpVerify) {
        store.dispatch(logoutUserAction(null, false));
      }

      return Promise.reject(error);
    }
    case 403: {
      const tokenPresent = needTokenRefresh(error);

      if (tokenPresent) return tokenPresent;

      if (error.response.request.responseURL.indexOf('offer/unlock')) {
        store.dispatch(setMailError(status, errorMessage));
        return Promise.reject({ error });
      }
      return Promise.reject(error);
    }
    case 409: {
      reloadAndFetchUpdatedResource(error);
      return Promise.reject(error);
    }
    case 422: {
      handleFormError(error);
      return Promise.reject(error);
    }
    case 400: {
      handleBadRequest(error, login, logout);
      return Promise.reject(error);
    }
    case 429: {
      handleTooManyRequest(error);
      return Promise.reject(error);
    }

    case 404: {
      return Promise.reject(error);
    }

    default:
      store.dispatch(
        displayFailMessage({
          info: 'Uh oh, something went wrong. Please refresh the page',
        })
      );
      return Promise.reject(error);
  }
};

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    const { pathname } = window.location;

    if (!error.response && !error.status && error.message === 'Network Error') {
      if (pathname === '/login') {
        store.dispatch(
          storeSpecificErrors('form', {
            errorCode: 'Network Error',
          })
        );
      }

      if (pathname === '/join') {
        store.dispatch(
          displayFailMessage(
            {
              info: (
                <span>
                  Sorry, we have experienced an issue loading the content.
                  <br />
                  Please{' '}
                  <span
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => window.location.reload()}
                  >
                    click here
                  </span>{' '}
                  to retry.
                </span>
              ),
            },
            true
          )
        );
      }

      return Promise.reject(error);
    }

    // store the apiVersion header present in API response
    if (!error.config) return Promise.reject(error);

    // Handle refresh token endpoint errors with better logic
    const isRefreshRequest =
      error.config.url.indexOf('auth/refresh') > 0 || error.config.url.indexOf('auth/refresh-token') > 0;

    if (isRefreshRequest) {
      // Only logout on actual authentication failures (401, 403)
      // Don't logout on network errors, timeouts, or cancelled requests
      const isAuthFailure = error.response?.status === 401 || error.response?.status === 403;
      const isNetworkError = !error.response;
      const isCancelled = error.code === 'ERR_CANCELED' || error.code === 'ECONNABORTED';
      const isTimeout = error.code === 'ECONNABORTED' && error.message?.includes('timeout');

      if (isAuthFailure) {
        // Genuine auth failure - logout user
        console.warn('Token refresh failed: Authentication error');
        store.dispatch(logoutUserAction(null, false));
        return Promise.reject(error);
      }

      if (isNetworkError || isCancelled || isTimeout) {
        // Network issue, cancelled request, or timeout - don't logout
        console.warn('Token refresh failed: Network/timeout issue, not logging out', {
          isNetworkError,
          isCancelled,
          isTimeout,
          errorCode: error.code,
        });
        return Promise.reject(error);
      }

      // Other errors from refresh endpoint - logout to be safe
      store.dispatch(logoutUserAction(null, false));
      return Promise.reject(error);
    }

    if (typeof error.response === 'undefined') {
      return Promise.reject(error);
    }

    if (error.response.status === 503) {
      if (error.response.data !== undefined && error.response.data.maintenance_mode) {
        window.location.href = 'http://maintenance.whatsyourprice.com';
        return window.location.href;
      }
    }

    /** handle mail page invalid/suspended user */
    if (
      error.response.status === 404 &&
      error.config.url.indexOf('thread') > -1 &&
      window.location.href.indexOf('mail') > -1
    ) {
      store.dispatch(
        displayFailMessage({
          info: 'This conversation is no longer available or never existed.',
        })
      );
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      (error.config.url.indexOf('/liveness/magic-link/verify') || /\/liveness\/\w+\/submit/.test(error.config.url)) > 0
    ) {
      return Promise.reject(error);
    }
    /** handle error code */
    // @TODO fully migrate all error handling to using error codes
    const errorIntercepted = interceptError(error);

    if (errorIntercepted) {
      return Promise.reject(error);
    }

    // handle response error status below
    return handleErrorStatus(error);
  }
);

/**
 Retrieves JWToken
 @param login
 @param password
 **/
export function getAccountToken(login: any, password: any): AxiosPromise {
  return axios({
    method: 'post',
    url: `${api}/auth/login`,
    data: { login, password },
  });
}

export function promoCodeCheck(
  hashId: string,
  promoCode: any,
  packageId: any,
  isPreJoin?: boolean,
  country?: string | null,
  withPackage = false,
  paymentChannel = null
): AxiosPromise {
  let url = `${api}/promo-code/check/${hashId}?promo_code=${promoCode}&package_id=${packageId}`;

  if (isPreJoin) {
    url += '&prejoin=true';
  }

  if (country) {
    url += `&billing_country=${country}`;
  }

  if (withPackage) {
    url += '&with_package=true';
  }

  if (paymentChannel) {
    url += `&payment_channel=${paymentChannel}`;
  }

  return axios({
    method: 'GET',
    url: url,
  });
}

/**
 Gets favorites for dashboard
 **/
export function profileDashboardFavorites(limit = 8): AxiosPromise {
  return axios({
    method: 'get',
    url: `${api}/favorite/featured?include=other_account.profile&limit=${limit}`,
    data: {},
  });
}

/**
 Gets boosted profiles for dashboard
 **/
export function profileDashboardBoosted(limit = 4, distance = 50, order = 'random'): AxiosPromise {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'GET',
    url: `${searchMicroserviceUrl}/featured?include=account&limit=${limit}&distance=${distance}&order=${order}`,
    withCredentials: false,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-client-id': searchMicroserviceKey,
    },
  });
}

/**
 * Store account email password
 *
 * @param {object} data
 * @returns Axios
 */
export function postAccountEmailPassword(data: any): AxiosPromise {
  return axios({
    method: 'PUT',
    url: `${api}/settings/add-email-account`,
    data,
  });
}

/**
 * Update account email password
 *
 * @param {object} data
 * @returns Axios
 */
export function updateAccountEmailPassword(data: any): AxiosPromise {
  return axios({
    method: 'PUT',
    url: `${api}/settings/update-email-account`,
    data,
  });
}

/**
 * Search profiles
 *
 * @param {object} formData
 * @param {boolean} fetchFirst
 * @param {boolean} refTimestamp
 */
export function profileSearch(formData = null, fetchFirst = true, refTimestamp = false, pgid = null): any {
  const { meta, search } = store.getState();

  // if no custom data is given pull out the user defined filters
  if (formData === null || typeof formData !== 'object' || Object.keys(formData).length === 0) {
    formData = search.filters;
  }

  if (refTimestamp && formData.ref_timestamp !== undefined) delete formData.ref_timestamp;

  if (refTimestamp && meta.search_timestamp && meta.search_timestamp > 0 && !fetchFirst) {
    formData.ref_timestamp = meta.search_timestamp;
  }

  if (pgid !== null || typeof pgid === 'undefined') {
    formData.pgid = pgid;
  }

  const data = {
    include: 'account',
    page: fetchFirst ? 1 : meta.search_current_page + 1,
    per_page: perPage,
    ...SearchConfig.filters,
    ...formData,
    has_photo: true,
  };

  const url = `${api}/profile?${qs.stringify(cleanObject(data))}`;

  if (searchMicroserviceEnabled === true) {
    const accessToken = getAccessToken();

    if (!accessToken) {
      return Promise.reject({ message: 'Access token not available' });
    }

    return axios({
      method: 'GET',
      url: `${searchMicroserviceUrl}?${qs.stringify(cleanObject(data))}`,
      withCredentials: false,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'x-client-id': searchMicroserviceKey,
      },
    });
  }

  return axios({
    method: 'GET',
    url: url,
  });
}

export function getSearch(formData: any = null, page = 1, refTimestamp = false, pgid = null): AxiosPromise {
  const { search, meta } = store.getState();

  // if no custom data is given pull out the user defined filters
  if (formData === null || typeof formData !== 'object' || Object.keys(formData).length === 0) {
    formData = search.filters;
  }

  if (refTimestamp && formData.ref_timestamp !== undefined) delete formData.ref_timestamp;

  const data: any = {
    include: 'account',
    page,
    per_page: perPage,
    ...SearchConfig.filters,
    ...formData,
    pgid: pgid,
    has_photo: true,
  };

  if (page > 1 && refTimestamp && meta.search_timestamp && meta.search_timestamp > 0) {
    data.ref_timestamp = meta.search_timestamp;
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    return Promise.reject({ message: 'Access token not available' });
  }

  return axios({
    method: 'GET',
    url: `${searchMicroserviceUrl}?${qs.stringify(cleanObject(data))}`,
    withCredentials: false,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-client-id': searchMicroserviceKey,
    },
  });
}

/**
 * Actualy POST request to unlink the email to an account
 * @param {Object} formData
 * @returns {AxiosPromise}
 */
export function unlinkEmailToAccount(formData: any): AxiosPromise {
  return axios({
    method: 'POST',
    url: `${api}/unlink/${formData.accountId}`,
    data: formData,
  });
}

/**
 * GET request to retrieve account to unlink
 * @param {Object} data
 * @returns {AxiosPromise}
 */
export function getAccountToUnlink(data: any): AxiosPromise {
  return axios({
    method: 'GET',
    url: `${api}/unlink?data=${data.data}`,
  });
}

/**
 * Sends support ticket to deskpro
 *
 * @param object formData
 */
export function sendSupportTicket(formData: any): AxiosPromise {
  return axios({
    method: 'POST',
    url: `${api}/ticket`,
    data: formData,
  });
}

export function signupCheck(data: any): AxiosPromise {
  const deviceIdentifier = getUniqueDevice();

  if (deviceIdentifier.request_id) {
    data.ipqs_device_id = deviceIdentifier.device_id;
    data.ipqs_request_id = deviceIdentifier.request_id;
  }

  return axios({
    method: 'POST',
    url: `${api}/auth/signup/check`,
    data,
  });
}

export function subscribeWebPush(subscription: any): AxiosPromise {
  return axios({
    method: 'POST',
    url: `${api}/webpush/subscription`,
    data: subscription,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function unsubscribeWebPush(subscription: any): AxiosPromise {
  return axios({
    method: 'DELETE',
    url: `${api}/webpush/subscription`,
    data: subscription,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function getGlobalNotifications(): AxiosPromise {
  return axios({
    method: 'GET',
    url: `${api}/notification-banner`,
  });
}

export function triggerVideoCallEvent(hash_id: string, signal: any): AxiosPromise {
  return axios({
    method: 'POST',
    url: `${api}/thread/video-call/start/${hash_id}`,
    data: {
      signal,
    },
  });
}

export function updateVideoCallStatus(video_uid: string, status: any, reason = ''): AxiosPromise {
  return axios({
    method: 'PUT',
    url: `${api}/thread/video-call/status`,
    data: {
      video_uid,
      status,
      reason,
    },
  });
}

export function getPostalLookup(zipcode: any, country: string): AxiosPromise {
  return axios({
    method: 'GET',
    url: `${api}/zipcodes?zipcode=${zipcode}&country=${country}`,
  });
}

export function saveLastSearch(formData: any, fetchFirst = true): AxiosPromise {
  const { meta } = store.getState();

  const searchData = {
    include: 'account',
    page: fetchFirst ? 1 : meta.search_current_page + 1,
    per_page: perPage,
    ...SearchConfig.filters,
    ...formData,
    has_photo: true,
  };

  return axios({
    method: 'POST',
    url: `${api}/profile/save-last-search`,
    data: searchData,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function fetchSurveyQuestions(title: string): AxiosPromise {
  return axios({
    method: 'GET',
    url: `${api}/survey/${title}`,
  });
}

export const {
  fetchEvalFeature,
  doWebTimestampCheck,
  sendEvidentlyMetrics,
  getEvidentlyExperiment,
  batchEvaluateFeature,
} = common;

export const {
  postMessage,
  sendPoke,
  toggleArchive,
  sendTypingIndicator,
  deletePhotoMessage,
  reportPhotoMessage,
  getMessagingConversationList,
  getMessagingConversationByUid,
  preUploadPhotoMessage,
  getUnreadCount,
} = mail;

export const {
  getBillingPackages,
  getKountDetails,
  getBillingDescriptors,
  postBillingPayment,
  getBillingPayments,
  getPaymentToken,
  deleteSavedPaymentToken,
  logPaymentVisit,
  getIDVPackages,
  postIDVPayment,
  postDDC,
} = billing;

export const {
  createUserAccountV2,
  createUserAccount,
  getNewAuthToken,
  killAuthToken,
  requestPassReminder,
  getFirstAuthToken,
  setNewPassword,
  validateActivationPasswordToken,
  validateResetPasswordToken,
  deactivateAccount,
  resendEmail,
  attemptSocialLogin,
  attachSocialLogin,
  validateSocialCredentials,
  getSignedLinkToken,
  destroyOtherDeviceSession,
  validateAutoSignup,
  getVerificationStatus,
  setVerificationStatus,
  requestOtp,
  requestOtpVerify,
  requestOtpAuth,
  requestOtpAuthVerify,
  verifyEmail,
  requestEmailOtp,
  resetEmailOtp,
  emailOtpVerify,
  verifyNewEmail,
  authVerifyPassword,
  postSelfieVerification,
  updateFacelivenessResult,
  generateMagicLink,
  verifyMagicLink,
  selfieVerificationSkip,
} = auth;

export const { getFavorites, getInterested, getInterestedV2, ignoreInterested, toggleFavorite, markAsReadInterested } =
  favourites;

export const {
  getOffers,
  createNewOffer,
  counterOffer,
  acceptOffer,
  ignoreOffer,
  sendSuggestion,
  unlockOffer,
  updateOffer,
  quickOffer,
} = offer;

export const {
  createUserProfile,
  getLoggedProfile,
  getProfileMapFields,
  getSignedEmailPromoToken,
  getLoggedAccount,
  updateProfileCoverData,
  updateProfileOpeningOfferData,
  updateProfileAboutData,
  updateProfileInterestData,
  updateProfileDescriptionData,
  updateProfileSeekingData,
  updateLoggedProfile,
  getAccountByUserName,
  getExternalProfile,
  getAccountSettings,
  getVideoChatPreferences,
  getTurnCredentials,
  getCheckInfoRequest,
  requestUserInfo,
  getBlockedList,
  getDismissedList,
  editBiometricName,
  updateAccountSettings,
  updateVideoChatPreferences,
  gdprDelete,
  gdprAcknowledged,
  mockProcessPhoto,
  postNewPhoto,
  uploadToS3,
  getPhoto,
  processPhoto,
  deletePhoto,
  putPhotoAvatar,
  putPhotoPrivacy,
  reportUser,
  getReportReasons,
  blockUser,
  dismissUser,
  getCredits,
  getMandatoryCompletion,
  updateMandatoryCompletion,
  verifyPassword,
  updatePhoneNumber,
  updateProfileNewJoinFlow,
  unlinkPhoneNumber,
} = profile;

export const {
  deleteBiometric,
  getBiometricKeys,
  getCredentialPublicKey,
  registerBiometric,
  getAuthenticationPublicKey,
  authenticateBiometric,
} = webAuthn;

export const { mixpanelTrack } = mixpanel;

export const { addNewMedia, markUploadFailed, deleteMedia, updateMediaPrivacy, bulkDelete, bulkTogglePrivacy } =
  gallery;

export const {
  subscribeRecurringPayment,
  unSubscribeRecurringPayment,
  getRecurringPayment,
  updateStateBasedSettings,
  resetHiddenDialogs,
  settingsVerifyPassword,
  changeAccountEmail,
} = settings;
